import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Download,
  Share2,
  FileText,
  Printer,
  Plus,
  Trash2,
} from "lucide-react";
import { sampleMaterials } from "@/data/sampleMaterials";
import { toast } from "sonner";

interface BOMItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  supplier: string;
  notes: string;
}

export default function BOM() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState<BOMItem[]>(
    sampleMaterials.slice(0, 5).map((m, i) => ({
      id: m.id,
      name: m.name,
      category: m.category,
      quantity: Math.floor(Math.random() * 50) + 10,
      unit: m.priceUnit,
      unitPrice: m.price,
      totalPrice: m.price * (Math.floor(Math.random() * 50) + 10),
      supplier: "Premium Materials Co.",
      notes: "",
    }))
  );

  const totalCost = items.reduce((sum, item) => sum + item.totalPrice, 0);

  const handleQuantityChange = (id: string, quantity: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, quantity, totalPrice: item.unitPrice * quantity }
          : item
      )
    );
  };

  const handleNotesChange = (id: string, notes: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, notes } : item))
    );
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    toast.success("Item removed from BOM");
  };

  const handleExportPDF = () => {
    toast.success("BOM exported as PDF");
  };

  const handleExportCSV = () => {
    toast.success("BOM exported as CSV");
  };

  const handleShare = () => {
    toast.info("Share link copied to clipboard");
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 lg:py-12">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-semibold text-foreground">
                Bill of Materials
              </h1>
              <p className="text-muted-foreground">
                Modern Loft Renovation • {items.length} items
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-2">
              <FileText className="h-4 w-4" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-2">
              <Download className="h-4 w-4" />
              PDF
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Printer className="h-4 w-4" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleShare} className="gap-2">
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          </div>
        </div>

        {/* Summary Card */}
        <div className="mb-8 grid gap-4 sm:grid-cols-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">Total Items</p>
            <p className="mt-1 font-serif text-2xl font-semibold text-foreground">
              {items.length}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">Categories</p>
            <p className="mt-1 font-serif text-2xl font-semibold text-foreground">
              {new Set(items.map((i) => i.category)).size}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">Suppliers</p>
            <p className="mt-1 font-serif text-2xl font-semibold text-foreground">
              {new Set(items.map((i) => i.supplier)).size}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-primary/10 p-6">
            <p className="text-sm text-primary">Total Estimated Cost</p>
            <p className="mt-1 font-serif text-2xl font-semibold text-primary">
              ${totalCost.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Material</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        handleQuantityChange(item.id, parseInt(e.target.value) || 0)
                      }
                      className="h-8 w-20 text-right bg-background"
                    />
                    <span className="ml-1 text-xs text-muted-foreground">{item.unit}</span>
                  </TableCell>
                  <TableCell className="text-right">${item.unitPrice}</TableCell>
                  <TableCell className="text-right font-medium">
                    ${item.totalPrice.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{item.supplier}</TableCell>
                  <TableCell>
                    <Input
                      value={item.notes}
                      onChange={(e) => handleNotesChange(item.id, e.target.value)}
                      placeholder="Add notes..."
                      className="h-8 bg-background"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Add Item Button */}
        <div className="mt-4">
          <Button variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />
            Add Material
          </Button>
        </div>
      </div>
    </Layout>
  );
}
